import type { Deal, DealStage } from '@/types'
import { formatDate, formatTime } from '@/utils/format-date'
import { getMeetingTiming } from '@/components/Deals/DealDetailPanel/reuniaoMeetingUtils'
import { isSlaBreached } from '@/components/Deals/kanbanColumnMetrics'

export type DealRiskIconKind = 'ban' | 'clock' | 'alert' | 'trending'

export type DealMeetingSchedule = {
  date: string
  time: string
  relative: string | null
  tone: 'past' | 'future' | 'now' | null
}

export function getDealMeetingSchedule(deal: Deal): DealMeetingSchedule | null {
  if (deal.etapa !== 'reuniao_agendada') return null

  const raw = deal.reunioes?.[0]?.data ?? deal.dataReuniao
  if (!raw) return null

  const date = formatDate(raw)
  const time = formatTime(raw)
  if (date === '—' || time === '—') return null

  const timing = getMeetingTiming(raw)

  return {
    date,
    time,
    relative: timing?.label ?? null,
    tone: timing?.tone ?? null,
  }
}

export function getNextStepV2(deal: Deal) {
  const meetingSchedule = getDealMeetingSchedule(deal)
  if (meetingSchedule) {
    return `${meetingSchedule.date} · ${meetingSchedule.time}`
  }

  const steps: Partial<Record<DealStage, string>> = {
    qualificado: 'Agendar reunião',
    reuniao_agendada: 'Confirmar agenda',
    reuniao_realizada: 'Elaborar proposta',
    proposta_elaboracao: 'Enviar proposta',
    proposta_enviada: 'Fazer follow-up',
    teste_gratis: 'Retomar após o teste',
    renegociacao: 'Resolver objeções',
    ajustar_proposta: 'Ajustar proposta',
    gerar_contrato: 'Gerar contrato',
    contrato_enviado: 'Marcar Handover',
    handover: 'Concluir handover',
    ganho: 'Ativar pós-venda',
    perdido: 'Registrar aprendizado',
    pausado: 'Definir retomada',
    fechamento_futuro: 'Programar contato',
  }
  return steps[deal.etapa] ?? 'Atualizar etapa'
}

function isDateOverdue(date?: string) {
  if (!date) return false
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  parsed.setHours(0, 0, 0, 0)
  return parsed < today
}

export function getDealRiskV2(deal: Deal): {
  label: string
  tone: 'info' | 'warning' | 'danger' | null
  icon: DealRiskIconKind | null
} {
  if (deal.etapa === 'ajustar_proposta') {
    return {
      label: 'Ajustar proposta',
      tone: 'warning',
      icon: 'alert',
    }
  }
  if (deal.financeAwaitingCloser && deal.etapa === 'gerar_contrato') {
    return {
      label: 'Financeiro aguardando você',
      tone: 'info',
      icon: 'alert',
    }
  }
  if (deal.motivoPerda) {
    return { label: 'Perda registrada', tone: 'danger', icon: 'ban' }
  }
  if (isDateOverdue(deal.dataFechamentoPrevisto)) {
    return { label: 'Fechamento vencido', tone: 'danger', icon: 'clock' }
  }
  if (deal.objecoes) {
    return { label: 'Objeção', tone: 'warning', icon: 'alert' }
  }
  if (isSlaBreached(deal)) {
    return { label: `${deal.driftDays}d parado`, tone: 'warning', icon: 'clock' }
  }
  if (
    deal.probabilidade < 45 &&
    ['proposta_enviada', 'renegociacao', 'gerar_contrato'].includes(deal.etapa)
  ) {
    return { label: 'Prob. baixa', tone: 'warning', icon: 'trending' }
  }
  return { label: '', tone: null, icon: null }
}
